<template>
  <div
    ref="sliderTrack"
    class="chart-slider"
    @touchmove="onTouchMove"
    @touchend="onMouseUp"
  >
    <div class="slider-track"></div>
    <div
      v-for="(val, index) in localValues"
      v-show="!disabled"
      :key="index"
      class="slider-handle"
      :style="{ bottom: val + '%' }"
      :class="{ 'is-dragging': draggingIndex === index }"
      @mousedown="startDrag(index, $event)"
      @touchstart="startDrag(index, $event)"
    ></div>
  </div>
</template>

<script>
import { ref, computed } from '@vue/composition-api'

export default {
  props: {
    value: {
      type: Array,
      required: true,
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 100,
    },
    step: {
      type: Number,
      default: 5,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const sliderTrack = ref(null)
    const draggingIndex = ref(null)
    const localValues = computed(() => [...props.value].sort((a, b) => a - b))

    const getValueFromPosition = clientY => {
      if (!sliderTrack.value) return 0
      const rect = sliderTrack.value.getBoundingClientRect()
      const percentage = ((rect.bottom - clientY) / rect.height) * 100

      return Math.round(percentage / props.step) * props.step
    }

    const constrainValue = (value, index) => {
      let constrained = Math.max(props.min, Math.min(props.max, value))

      const minGap = props.step
      if (index > 0) {
        constrained = Math.max(constrained, localValues.value[index - 1] + minGap)
      }
      if (index < localValues.value.length - 1) {
        constrained = Math.min(constrained, localValues.value[index + 1] - minGap)
      }

      return constrained
    }

    const updateValue = clientY => {
      if (draggingIndex.value === null) return

      const newValue = getValueFromPosition(clientY)
      const sortedIndex = [...props.value].sort((a, b) => a - b).indexOf(props.value[draggingIndex.value])
      const constrainedValue = constrainValue(newValue, sortedIndex)

      const newValues = [...props.value]
      newValues[draggingIndex.value] = constrainedValue
      emit('input', newValues)
    }

    const onMouseMove = event => updateValue(event.clientY)

    const onMouseUp = () => {
      draggingIndex.value = null
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }

    const startDrag = (index, event) => {
      if (props.disabled) return
      event.preventDefault()
      draggingIndex.value = index
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    }

    const onTouchMove = event => {
      if (draggingIndex.value === null) return
      updateValue(event.touches[0].clientY)
    }

    return {
      sliderTrack,
      localValues,
      draggingIndex,
      startDrag,
      onMouseMove,
      onTouchMove,
      onMouseUp,
    }
  },
}
</script>

<style scoped lang="scss">
.chart-slider {
  position: relative;
  width: 13px;
  height: 75vh;
  flex-shrink: 0;
  cursor: default;
}

.slider-track {
  position: absolute;
  left: 6px;
  top: 0;
  bottom: 0;
  width: 1px;
  background-color: black;
}

.slider-handle {
  position: absolute;
  left: 3px;
  width: 8px;
  height: 15px;
  border-radius: 999px;
  background-color: black;
  cursor: ns-resize;
  transform: translateY(50%);
  transition: transform 0.1s ease;
  z-index: 1;

  &:hover {
    transform: translateY(50%) scale(1.1);
  }

  &.is-dragging {
    transform: translateY(50%) scale(1.2);
    z-index: 2;
  }
}
</style>
